import React, { useEffect, useRef, useState, MouseEvent, useContext } from 'react';
import { ModuleBaseModel, ModuleBasePropsModel } from '@/types';
import {
  AppInsightsContext,
  CMSModule,
  MetadataContext,
  TrackEventKey,
  TrackEventName,
  TrackEventType,
  UserContext,
} from '@/components';
import classNames from 'classnames';
import { useRouter } from 'next/router';

type TabbedNavigationModuleModel = ModuleBaseModel<
  {
    layout?: 'Normal' | 'Full width' | 'ICP';
    tabs?: {
      anchor: string;
      moduleName: string;
      tabModules: ModuleBaseModel<ModuleBasePropsModel>[];
      tabName: string;
    }[];
  } & ModuleBasePropsModel
>;

const TabbedNavigationModule = (data: TabbedNavigationModuleModel) => {
  const {
    props: { anchor, tabs, layout = 'Normal' },
  } = data;
  const router = useRouter();
  const appInsights = useContext(AppInsightsContext);
  const user = useContext(UserContext);
  const { pageId } = useContext(MetadataContext);

  const [underlineStyle, setUnderlineStyle] = useState({});
  const [activeTabIndex, setActiveTabIndex] = useState<number>(
    router.query.tab !== undefined && tabs
      ? tabs?.findIndex((tab) => tab.anchor === (router.query.tab as string)) > -1
        ? tabs?.findIndex((tab) => tab.anchor === (router.query.tab as string))
        : 0
      : 0,
  );
  const tabsContainer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    moveUnderline();
    restoreActiveTab();
    window.addEventListener('resize', moveUnderline);
    return () => window.removeEventListener('resize', moveUnderline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    moveUnderline();
  }, [activeTabIndex]);

  const moveUnderline = () => {
    setTimeout(() => {
      if (tabsContainer?.current?.children) {
        Array.from(tabsContainer.current.children).forEach((element) => {
          if (element && element.classList && element.classList.contains('active-tab')) {
            const el = element as HTMLElement;
            setUnderlineStyle({ left: `${el.offsetLeft}px`, width: `${el.offsetWidth}px` });
          }
        });
      }
    }, 0);
  };

  const tabClick = (e: MouseEvent<HTMLAnchorElement>, tabIndex: number, tabName: string) => {
    e.preventDefault();
    setActiveTabIndex(tabIndex);
    saveActiveTab(tabIndex);
    appInsights.trackCustomEvent(TrackEventName.REPORT, TrackEventKey.TAB, TrackEventType.SELECTED, user, {
      pageId,
      pagePath: router.asPath,
      tabTitle: tabName,
    });
  };

  const saveActiveTab = (tabIndex: number) => {
    try {
      sessionStorage.setItem(`active-tab:${anchor}:${router.asPath}`, tabIndex.toString());
    } catch {
      // ignore
    }
  };

  const restoreActiveTab = () => {
    const activeTab = sessionStorage.getItem(`active-tab:${anchor}:${router.asPath}`);
    if (activeTab && !router.query.tab && tabs && parseInt(activeTab, 10) < tabs.length) {
      setActiveTabIndex(parseInt(activeTab, 10));
    }
  };

  if (!tabs) return null;

  return (
    <div id={anchor} className="TabbedNavigationModule overflow-hidden">
      <div className={classNames('container flex justify-center', layout === 'ICP' ? 'px-0 lg:px-9' : 'px-3')}>
        <div
          className={classNames(
            'relative my-3 flex w-full overflow-x-auto overflow-y-hidden lg:justify-center lg:overflow-visible',
            (layout === 'Normal' || !layout) && '',
            layout === 'Full width' && 'border-b border-[#ccc]',
            layout === 'ICP' && 'lg:before-extended-line-top lg:after-extended-line-bottom border-y lg:border-y-0',
          )}
          ref={tabsContainer}
        >
          {tabs?.map((tab, index) => (
            <a
              key={index}
              id={tab.anchor}
              href={'#' + tab.anchor}
              className={classNames(
                'flex px-3 transition-colors',
                tabs.length - 1 === index && layout === 'ICP' && 'lg:pr-0',
                (layout === 'Normal' || !layout) &&
                  'h-12 w-full max-w-[360px] min-w-[150px] items-center justify-center border-b border-[#ccc] px-5 text-center hover:bg-gray-100 md:px-10 lg:whitespace-nowrap',
                layout === 'Full width' &&
                  'h-12 w-full items-center justify-center whitespace-nowrap hover:bg-gray-100',
                layout === 'ICP' && 'w-full min-w-[170px] uppercase lg:min-w-[150px] lg:first:pl-0',
                activeTabIndex === index && 'active-tab',
              )}
              onClick={(e) => tabClick(e, index, tab.tabName)}
            >
              <div
                className={classNames(
                  'cursor-pointer',
                  layout === 'ICP'
                    ? 'text-style-tag xxl:w-[70%] py-6 text-[16px] leading-[16px] lg:w-[100%] lg:text-[19px] lg:leading-[22px] xl:w-[80%] xl:text-[22px] xl:leading-[24px]'
                    : 'text-lg leading-[22px] font-bold',
                )}
              >
                {tab.tabName}
              </div>
            </a>
          ))}
          <div
            className={classNames(
              'absolute transition-all duration-500 ease-out',
              layout === 'ICP' ? 'bg-red bottom-0 h-1.5' : '-bottom-px h-0.5 bg-black',
            )}
            style={underlineStyle}
          />
        </div>
      </div>
      <div>
        {tabs?.map((tab, index) => (
          <div
            key={index}
            className={classNames('flex w-full flex-col', activeTabIndex === index ? 'block' : 'hidden')}
          >
            {tab.tabModules?.map((module) => (
              <CMSModule key={module.id} module={module} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TabbedNavigationModule;
